import {
    Form,
    Formik,
    FormikProps,
} from "../../lib/formik/Formik";
import {
    Fragment,
    useEffect,
    useRef,
    useState
} from "react";
import { dematDetailsSchema } from "../../utils/schema";
import { RootStateType } from "../../redux-store/reducers";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import {
    ProceedSaveLater,
    SubHeading
} from "./components";
import {
    applicationComparison,
    getApplicantName,
    applyRoleBasedStatus,
    saveForLater,
    isModuleAllowed
} from "../../utils/utilityFunctions";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MFSelectField from "../../lib/formik/SelectField";
import FormikSearchDropdown from "../../lib/formik/FormikSearchDropdown";
import { MFTextField } from "../../lib/formik";
import MFCheckbox from "../../lib/formik/Checkbox";
import { Demat } from "../../redux-store/types/api-types";
import {
    APPLICATION_TYPE,
    AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
    USER_ROLES
} from "../../utils/constant";
import {
    useHistory,
    useRouteMatch
} from "react-router";
import { updateApplication } from "../../redux-store/actions/application";
import { useSnackbar } from "notistack";
import { showError } from "../../redux-store/actions/auth";
import LoadingButton from '@mui/lab/LoadingButton';

const MAX_DEMAT_DETAILS_SECTIONS = 5

const DEPOSITORY_NAME_LIST = [
    {
        key: "CDSL",
        value: "CDSL"
    }, {
        key: "NSDL",
        value: "NSDL"
    }
]

const dematDetailObject: Demat = {
    depositoryName: "NSDL",
    dpName: null,
    dpId: "",
    clientId: "",
    defaultDematAccount: false,
    isActive: true
};

type Values = {
    status?: string;
    applicationType?: string;
    demats: Demat[];
    saveType: string;
};

const initialValues: Values = {
    status: '',
    applicationType: '',
    demats: [dematDetailObject],
    saveType: 'save and proceed',
};

const dpAccountDetailsMap: Record<string, string> = {
    1: 'First',
    2: 'Second',
    3: 'Third',
    4: "Fourth",
    5: "Fifth"
}

export default function DematDetails(): JSX.Element {
    const theme = useTheme()
    const history = useHistory();
    const { path: currentPath } = useRouteMatch();
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();

    const formikRef = useRef<FormikProps<Values>>(null);

    const [dematDetailsData, setDematDetailsData] = useState(initialValues);
    const [dematDetailToDelete, setDematDetailToDelete] = useState<{ dematDetail: Demat; index: number } | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const { application } = useSelector((store: RootStateType) => store.application);
    const { id = '' } = application || {};

    const { role = '' } = useSelector((store: RootStateType) => store.auth);

    const isDiyInvestorOnboarding = currentPath?.includes("/investor-onboarding-application")

    useEffect(() => {
        const { demats = [], applicationType, applicants = [] } = application || {};

        setDematDetailsData((prevState) => ({
            ...prevState,
            applicationType: applicationType,
            status: applicants.length ? applicants[0].status : '',
            demats: demats.length
                ? demats.map((demat) => ({
                    ...demat,
                    depositoryName: demat?.depositoryName ?? "",
                    dpName: demat?.dpName ? { label: demat.dpName, value: demat.dpName } : null,
                    dpId: demat?.dpId ?? "",
                    clientId: demat?.clientId ?? "",
                    defaultDematAccount: !!demat?.defaultDematAccount
                }))
                : [{ ...dematDetailObject, applicationId: id, defaultDematAccount: true }],
        }));
    }, [application]);

    const handleSubmit = async (values: Values) => {
        try {
            const { demats, saveType } = values;

            const {
                demats: existingDemats = [],
                id,
                applicant1ReferenceId = '',
                currentStep,
                applicationType,
                status,
                hasPOA,
                applicationNumber,
            } = application || {};

            const updatedDemats = demats.map((demat: Demat, ind: number) => ({
                ...demat,
                dpName: demat?.dpName?.value ?? ""
            }));

            const checkApplication = applicationComparison(
                {
                    ...application,
                    demats: application?.demats?.sort((demat1, demat2) => Number(demat1.id) - Number(demat2.id)),
                },
                {
                    ...application,
                    demats: updatedDemats
                        .sort((demat1, demat2) => Number(demat1.id) - Number(demat2.id)),
                    currentStep:
                        applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                            ? !!currentStep && currentStep > 4
                                ? currentStep
                                : Number(currentStep) + 1
                            : !!currentStep && currentStep > 8
                                ? currentStep
                                : Number(currentStep) + 1,
                }
            );
            const isSaveLater = saveType !== 'save and proceed';

            let currentStatus = status

            if (
                !isModuleAllowed({ value: (role ?? ""), compareArr: [USER_ROLES.AMCAPPROVER] }) &&
                status === 'sent_to_amc_approver'
            ) {
                currentStatus = 'draft'
            } else if (
                !hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                status !== 'draft' &&
                applyRoleBasedStatus(role) &&
                applicationType !== APPLICATION_TYPE.NON_INDIVIDUAL
            ) {
                currentStatus = 'sent_to_amc_approver'
            }

            if (id && !checkApplication) {
                setLoading(true);
                const response = await dispatch(
                    updateApplication({
                        body: {
                            ...application,
                            demats: updatedDemats,
                            status: currentStatus,
                            currentStep: applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? 5 : 9,
                        },
                        applicationId: id,
                        ...(isSaveLater && { toastMessage: '' }),
                    })
                );
                if (!response) {
                    setLoading(false);
                    return;
                }
                !isSaveLater
                    ? applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                        ? history.push('related-parties', { id, applicant1ReferenceId })
                        : history.push('risk-profiles', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId, isDiyInvestorOnboarding));
            } else if (checkApplication) {
                if (isSaveLater) {
                    enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                }
                !isSaveLater
                    ? applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                        ? history.push('related-parties', { id, applicant1ReferenceId })
                        : history.push('risk-profiles', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId, isDiyInvestorOnboarding));
            }
        } catch (e) {
            typeof e === 'string' && dispatch(showError(e));
            console.error((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        if (!formikRef.current) return

        const { values, setFieldValue } = formikRef.current ?? {}

        setFieldValue(
            'demats',
            values.demats.map((demat: Demat, ind: number) => {
                if (ind === index) {
                    return {
                        ...demat,
                        defaultDematAccount: !demat.defaultDematAccount,
                    };
                }
                return {
                    ...demat,
                    defaultDematAccount: false,
                };
            })
        )
    }

    const handleAddDematDetails = () => {
        if (!formikRef.current) return

        const { values, setFieldValue } = formikRef.current ?? {}

        setFieldValue('demats', [...values.demats, { ...dematDetailObject, applicationId: id }])
    }

    const handleSaveDematDetails = (type: string) => {
        if (!formikRef.current) return

        const { setFieldValue } = formikRef.current ?? {}

        setFieldValue('saveType', type)
    }

    const handleConfirmationDialogSave = async () => {
        try {
            if (!formikRef.current) return

            const { values, setFieldValue } = formikRef.current ?? {}

            const { id } = application || {};
            const remainedDematDetails = values.demats.map((demat, i) => {
                if (i === dematDetailToDelete?.index) {
                    return {
                        ...demat,
                        isActive: false,
                        defaultDematAccount: false,
                    };
                }
                return demat
            })

            const modifiedRemainedDematDetails = remainedDematDetails.map((demat, i) => {
                const firstActiveDematDetailsIndex = remainedDematDetails.findIndex((d) => d.isActive);

                const allDefaultDematAccountFalse = remainedDematDetails.every((d) => !d.defaultDematAccount);

                return (i === firstActiveDematDetailsIndex && allDefaultDematAccountFalse)
                    ? { ...demat, defaultDematAccount: true, dpName: demat?.dpName?.value ?? "" }
                    : { ...demat, dpName: demat?.dpName?.value ?? "" };
            })

            if (id && dematDetailToDelete?.dematDetail.id) {
                setLoading(true);

                let currentStatus = application?.status

                if (
                    !isModuleAllowed({ value: (role ?? ""), compareArr: [USER_ROLES.AMCAPPROVER] }) &&
                    application?.status === 'sent_to_amc_approver'
                ) {
                    currentStatus = 'draft'
                } else if (
                    !application?.hasPOA &&
                    AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                    application?.status !== 'draft' &&
                    applyRoleBasedStatus(role) &&
                    application?.applicationType !== APPLICATION_TYPE.NON_INDIVIDUAL
                ) {
                    currentStatus = 'sent_to_amc_approver'
                }

                await dispatch(
                    updateApplication({
                        body: {
                            ...application,
                            demats: modifiedRemainedDematDetails,
                            status: currentStatus,
                        },
                        applicationId: id,
                    })
                );
            } else {
                setFieldValue(
                    'demats',
                    modifiedRemainedDematDetails.filter((demat) => demat.isActive)
                );
            }

            setDematDetailToDelete(null);
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Formik
            innerRef={formikRef}
            initialValues={dematDetailsData}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={dematDetailsSchema}
        >
            {({ values }) => (
                <Form autoComplete='off'>
                    <Grid
                        container
                        rowSpacing={0.5}
                        columnSpacing={3}
                    >
                        {values.demats.map((dematDetail: Demat, index: number) => (
                            <Fragment key={index}>
                                <Grid item xs={12}>
                                    <SubHeading
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontFamily: `${theme.typography.fontFamily}`
                                        }}>
                                        {dpAccountDetailsMap[index + 1]} DP Account Details
                                        {values.demats.length > 1 && (
                                            <Button
                                                variant="text"
                                                endIcon={
                                                    <DeleteOutlineIcon
                                                        color={loading ? 'disabled' : 'error'}
                                                        fontSize="small"
                                                    />
                                                }
                                                sx={(theme: Theme) => ({
                                                    ...theme.typography.subtitle_600,
                                                    fontWeight: 400,
                                                    color: theme.palette.error[500],
                                                    padding: "0px",
                                                    "&:hover": {
                                                        backgroundColor: "transparent"
                                                    }
                                                })}
                                                onClick={() => setDematDetailToDelete({ dematDetail, index })}
                                                disabled={loading}
                                                aria-label='Delete'
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </SubHeading>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <MFSelectField
                                        name={`demats.${index}.depositoryName`}
                                        label="Depository Name"
                                        items={DEPOSITORY_NAME_LIST}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <FormikSearchDropdown
                                        name={`demats.${index}.dpName`}
                                        label="DP Name"
                                        options={[{
                                            label: "",
                                            value: ""
                                        }]}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <MFTextField
                                        name={`demats.${index}.dpId`}
                                        label={`DP ID ${values.demats[index].dpName ? "*" : ""}`}
                                        placeholder=""
                                        disabled={!values.demats[index].dpName || loading}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <MFTextField
                                        name={`demats.${index}.clientId`}
                                        label={`Client Id ${values.demats[index].dpName ? "*" : ""}`}
                                        placeholder=""
                                        disabled={!values.demats[index].dpName || loading}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ width: 'fit-content' }}>
                                        <MFCheckbox
                                            name={`demats.${index}.defaultDematAccount`}
                                            label={'Default DP Details'}
                                            sx={{ letterSpacing: '1px' }}
                                            onChange={(event) => handleCheckboxChange(event, index)}
                                            disabled={loading}
                                        />
                                    </Box>
                                </Grid>
                            </Fragment>
                        ))}
                        {values.demats.length < MAX_DEMAT_DETAILS_SECTIONS && (
                            < Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        fontSize: 14,
                                        mt: 2,
                                        mb: '40px',
                                        width: '200px'
                                    }}
                                    onClick={handleAddDematDetails}
                                    disabled={loading}
                                >
                                    Add
                                </Button>
                            </Grid>
                        )}
                        <ProceedSaveLater
                            saveLater={() => handleSaveDematDetails("save for later")}
                            saveAndProceed={() => handleSaveDematDetails("save and proceed")}
                            loader={loading}
                            clickedButton={values.saveType}
                            saveButtonText='Continue'
                            showEndIcon={false}
                        />
                        <Dialog
                            onClose={() => setDematDetailToDelete(null)}
                            open={dematDetailToDelete !== null}
                            maxWidth='sm'
                            fullWidth
                            sx={{
                                '& .MuiPaper-root': {
                                    borderRadius: '15px',
                                    backgroundColor: "background.paper",
                                    rowGap: "25px",
                                },
                            }}
                        >
                            <DialogContent sx={{ paddingTop: "25px" }}>
                                <Box sx={{ display: "flex", columnGap: "8px" }}>
                                    <Typography variant='h2' color="text.primary" mb="10px">
                                        {
                                            dematDetailToDelete?.dematDetail.defaultDematAccount
                                                ? 'Are you sure you want to delete default DP details'
                                                : 'Are you sure you want to delete DP details ?'
                                        }
                                    </Typography>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ padding: "25px", columnGap: "10px" }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setDematDetailToDelete(null)}
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.secondary,
                                        flex: 1,
                                        border: `0.5px solid ${theme.palette.text.secondary}`,
                                        padding: "10px",
                                        "&:hover, &.Mui-focusVisible": {
                                            borderColor: theme.palette.text.secondary,
                                            backgroundColor: theme.palette.background.default
                                        }
                                    })}
                                    disabled={loading}
                                >
                                    No
                                </Button>
                                <LoadingButton
                                    loadingPosition="start"
                                    loading={loading}
                                    variant="contained"
                                    fullWidth
                                    onClick={handleConfirmationDialogSave}
                                    sx={(theme) => ({
                                        flex: 1,
                                        padding: "10px",
                                        borderRadius: "10px",
                                        backgroundColor: theme.palette.primary[500],
                                        color: theme.palette.textDark.primary
                                    })}
                                >
                                    Yes
                                </LoadingButton>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </Form>
            )
            }
        </Formik >
    )
}
